import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, CalendarPlus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import GermanDateInput from "@/components/GermanDateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const err = (e) => toast.error(
  e.code === "ECONNABORTED"
    ? "Das Eintragen dauert länger als erwartet. Bitte die Seite neu laden – oft sind die Termine trotzdem angelegt."
    : formatApiErrorDetail(e.response?.data?.detail) || e.message,
);
const WD = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const weekday = (iso) => { const [y, m, d] = iso.split("-").map(Number); return WD[new Date(y, m - 1, d).getDay()] || ""; };
const addMin = (hhmm, min) => {
  const [h, m] = (hhmm || "09:00").split(":").map(Number);
  const t = Math.min(h * 60 + m + Math.round(min), 23 * 60 + 59);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

export function ScheduleSuggestButton({ clientId, coach, onApplied }) {
  const [items, setItems] = useState(null);
  const [msg, setMsg] = useState(null);
  const [mode, setMode] = useState("in_person");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/schedule-suggestions`, { coach_user_id: coach.coach_user_id });
      if (!r.data.items.length) { toast.info(r.data.message || "Keine Vorschläge möglich"); return; }
      setItems(r.data.items); setMsg(r.data.message);
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const upd = (i, patch) => setItems(items.map((x, j) => {
    if (j !== i) return x;
    const n = { ...x, ...patch };
    if ("planned_ue" in patch || "time_from" in patch) n.time_to = addMin(n.time_from, Number(n.planned_ue || 0) * 45);
    return n;
  }));
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/schedule-suggestions/apply`, {
        coach_user_id: coach.coach_user_id, mode, items: items.map((x) => ({ ...x, planned_ue: Number(x.planned_ue) })),
      }, { timeout: 120000 });
      toast.success(`${r.data.created} Termin(e) eingetragen`);
      r.data.errors.forEach((e) => toast.error(e));
      setItems(null); onApplied?.();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const sum = (items || []).reduce((a, x) => a + Number(x.planned_ue || 0), 0);
  return (
    <>
      <button type="button" onClick={load} disabled={busy} className="inline-flex items-center gap-1 border border-current/30 px-2 py-1 text-[11px] font-medium hover:bg-white/50 disabled:opacity-50" data-testid={`schedule-suggest-btn-${coach.coach_user_id}`}>
        {busy && !items ? <Loader2 size={11} className="animate-spin" /> : <CalendarPlus size={11} />} Termine vorschlagen
      </button>
      <Dialog open={!!items} onOpenChange={(o) => !o && setItems(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="schedule-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light tracking-tighter">Terminvorschläge · {coach.coach_name}</DialogTitle>
            <DialogDescription className="text-xs">{items?.length} Termine · {sum.toLocaleString("de-DE")} UE. Vor dem Eintragen anpassen oder einzelne Termine entfernen. Die Termine werden als „vorgeschlagen“ angelegt, der Kunde bestätigt sie.</DialogDescription>
          </DialogHeader>
          {msg && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2" data-testid="schedule-suggest-msg">{msg}</div>}
          <div className="space-y-1.5">
            {(items || []).map((x, i) => (
              <div key={i} className="grid grid-cols-[34px_1fr_1fr_1fr_70px_24px] gap-2 items-center" data-testid={`schedule-suggest-row-${i}`}>
                <span className="text-xs text-slate-500">{weekday(x.date)}</span>
                <GermanDateInput value={x.date} onChange={(e) => upd(i, { date: e.target.value })} className="input-tech" data-testid={`schedule-suggest-date-${i}`} />
                <input value={x.time_from} onChange={(e) => upd(i, { time_from: e.target.value })} placeholder="HH:MM" pattern="[0-2][0-9]:[0-5][0-9]" className="input-tech font-mono" data-testid={`schedule-suggest-from-${i}`} />
                <input value={x.time_to} onChange={(e) => upd(i, { time_to: e.target.value })} placeholder="HH:MM" pattern="[0-2][0-9]:[0-5][0-9]" className="input-tech font-mono" data-testid={`schedule-suggest-to-${i}`} />
                <input type="number" step="0.5" min="0.5" value={x.planned_ue} onChange={(e) => upd(i, { planned_ue: e.target.value })} className="input-tech text-right" data-testid={`schedule-suggest-ue-${i}`} />
                <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`schedule-suggest-del-${i}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 pt-3">
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="input-tech !w-44" data-testid="schedule-suggest-mode">
              <option value="in_person">Vor Ort</option><option value="online">Online</option>
            </select>
            <button onClick={apply} disabled={busy || !items?.length} className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2 text-sm hover:bg-black disabled:opacity-50" data-testid="schedule-suggest-apply">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CalendarPlus size={14} />} In Kalender eintragen
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
